import React from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2, icons } from "oolib";
import { UI_BODY_SM, UI_CAPTION, UI_TAG, UI_TITLE } from "oolib/dist/v2/components/Typo2";
import TooltipV2 from "@/components/UI/Tooltip";
import { SentimentBar } from "../comps/SentimentBar";

const { ArrowUp, ArrowDown, Minus, Info } = icons;

// Polarity colour tokens — mirror the v1 LensesTab constants (SENTIMENT_THEME /
// SIGNAL_COLORS) so the card looks identical, but kept local so LensesV2 stays
// self-contained (no dependency on the v1 dashboard we're replacing).
const SENTIMENT_THEME = {
  positive: { bg: "#6BDC58", glow: "#6BDC584D" },
  negative: { bg: "#FF7F7F", glow: "#FF6A6A4D" },
  neutral: { bg: "#03143033", glow: "#00000014" },
};
const SIGNAL_COLORS = { positive: "#3BA974", negative: "#E92519", neutral: colors2.grey50 };
const SIGNAL_SUPPORTIVE_COLORS = { positive: "#157704", negative: "#CF1616", neutral: colors2.grey40 };

const OverviewContainer = styled.div`
  display: flex;
  border-radius: 1.2rem;
  border: 1px solid ${colors2.grey10};
  background: ${colors2.white};
  overflow: hidden;
`;

const Card = styled.div`
  flex: 1;
  gap: 1.4rem;
  min-width: 0;
  padding: 1.4rem 1.6rem;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CardGlow = styled.span`
  position: absolute;
  top: -160px;
  left: -40px;
  width: 151px;
  height: 151px;
  border-radius: 145px;
  background: ${({ $color }) => $color};
  filter: blur(60px);
  pointer-events: none;
  z-index: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  position: relative;
  z-index: 1;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const SectionLabel = styled(UI_TAG)`
  color: ${colors2.grey50};
  text-transform: uppercase;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const StatusIconWrap = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatusValue = styled(UI_TITLE)`
  color: ${({ $color }) => $color};
`;

const ChangeText = styled(UI_CAPTION)`
  color: ${({ $color }) => $color};
`;

function CalculationTooltip({ basis }) {
  return (
    <div style={{ maxWidth: 300, padding: "10px 12px" }}>
      <UI_CAPTION style={{ color: colors2.white, fontWeight: 600 }}>{basis.formula}</UI_CAPTION>
      {(basis.groups || []).map(
        (group) =>
          group.signals?.length > 0 && (
            <div key={group.label} style={{ marginTop: 8 }}>
              <UI_TAG style={{ color: "rgba(255,255,255,0.6)", textTransform: "uppercase", display: "block", marginBottom: 2 }}>
                {group.label}
              </UI_TAG>
              <UI_CAPTION style={{ color: colors2.white, lineHeight: 1.5 }}>{group.signals.join(", ")}</UI_CAPTION>
            </div>
          )
      )}
    </div>
  );
}

// Resolve the state/trend colours exactly as the v1 overview card does: a
// declining trend always reads negative; an improving-but-bad state reads
// neutral; otherwise colour by good/bad state.
function resolveStateColors({ signal, trendDir }) {
  const state = signal === "negative" ? "bad" : "good";
  if (trendDir === "declining") {
    return { stateColor: SIGNAL_COLORS.negative, supportColor: SIGNAL_SUPPORTIVE_COLORS.negative };
  }
  if (trendDir === "improving" && state === "bad") {
    return { stateColor: SIGNAL_COLORS.neutral, supportColor: SIGNAL_SUPPORTIVE_COLORS.neutral };
  }
  if (state === "bad") {
    return { stateColor: SIGNAL_COLORS.negative, supportColor: SIGNAL_SUPPORTIVE_COLORS.negative };
  }
  return { stateColor: SIGNAL_COLORS.positive, supportColor: SIGNAL_SUPPORTIVE_COLORS.positive };
}

/**
 * The User Sentiment card — same look as the v1 overview card (glow, label +
 * calculation tooltip, trend status, period change, stacked polarity bar), fed
 * by our polarity buckets. Drilldown click-through is intentionally omitted for
 * now.
 */
export function SentimentCard({ label, signal = "neutral", trendDir = "stable", value, change, distribution, calculationBasis }) {
  const { stateColor, supportColor } = resolveStateColors({ signal, trendDir });
  const TrendIcon = trendDir === "improving" ? ArrowUp : trendDir === "declining" ? ArrowDown : Minus;
  const glow = (SENTIMENT_THEME[signal] || SENTIMENT_THEME.neutral).glow;

  return (
    <OverviewContainer>
      <Card>
        <CardGlow $color={glow} />

        <HeaderRow>
          <HeaderLeft>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <SectionLabel>{label}</SectionLabel>
              {calculationBasis && (
                <TooltipV2
                  primaryContent={{
                    layout: "custom",
                    render: <CalculationTooltip basis={calculationBasis} />,
                  }}
                >
                  <Info size={16} color={colors2.grey30} style={{ cursor: "pointer" }} />
                </TooltipV2>
              )}
            </div>

            <StatusRow>
              <StatusIconWrap $color={stateColor}>
                <TrendIcon size={16} weight="bold" color={colors2.white} />
              </StatusIconWrap>
              <StatusValue $color={stateColor}>{value}</StatusValue>
            </StatusRow>

            {change && <ChangeText $color={supportColor}>{change}</ChangeText>}
          </HeaderLeft>
        </HeaderRow>

        {distribution && (
          <SentimentBar
            showLegend
            segments={[
              { key: "positive", value: distribution.positivePercent, color: SENTIMENT_THEME.positive.bg },
              { key: "neutral", value: distribution.neutralPercent, color: SENTIMENT_THEME.neutral.bg },
              { key: "negative", value: distribution.negativePercent, color: SENTIMENT_THEME.negative.bg },
            ]}
          />
        )}
      </Card>
    </OverviewContainer>
  );
}

const SkeletonCard = styled.div`
  border-radius: 1.2rem;
  border: 1px solid ${colors2.grey10};
  background: ${colors2.white};
  padding: 1.4rem 1.6rem;
`;

export function SentimentCardSkeleton() {
  return (
    <SkeletonCard>
      <SkeletonLoader style={{ height: 10, width: 80, borderRadius: 4, marginBottom: 12 }} />
      <SkeletonLoader style={{ height: 18, width: 110, borderRadius: 4, marginBottom: 8 }} />
      <SkeletonLoader style={{ height: 10, width: 130, borderRadius: 4, marginBottom: 14 }} />
      <SkeletonLoader style={{ height: 10, width: "100%", borderRadius: 4, marginBottom: 6 }} />
      <SkeletonLoader style={{ height: 10, width: "90%", borderRadius: 4 }} />
    </SkeletonCard>
  );
}

export function SentimentCardError({ message }) {
  return (
    <SkeletonCard>
      <UI_BODY_SM style={{ color: "#dc2626" }}>Error: {message}</UI_BODY_SM>
    </SkeletonCard>
  );
}
